import { gapToleranceMs } from 'features/timeSeries/agentTab/modelSeries'
import type { SentinelBandOverlay } from 'features/timeSeries/hooks/useSentinelScores'
import { useMemo, useSyncExternalStore } from 'react'

/** How many sigmas wide the drawn band is when the model has no rule of its
 *  own. Matches the level alert's default. */
export const BAND_SIGMAS = 3

/** The band's colour. Read by the chart AND the hover readout, so the dashed
 *  line and the row that names its value are visibly the same thing. */
export const AGENT_BAND_COLOR = '#f5c542'

const DAY_MS = 86_400_000

/** A shaded span on the chart. Mirrors the studio's own private split-range
 * shape (startMs/endMs/role) that the band plugin already understands. */
interface ChartSplitRange {
  startMs: number
  endMs: number
  role: string
}

/**
 * The agent model currently being shown on the Process Data chart: its
 * baseline (reference) window and the after-baseline span, so the chart itself
 * shades WHERE the expectation was learned from — not just a picture in the
 * chat. Kept in a tiny module store rather than a provider so the Agent tab can
 * publish it without the chart having to know the tab exists.
 */
export interface AgentChartOverlay {
  /** The pen the band + regions hang off (the model's derived KPI pen). */
  target: string | null
  splitRanges: ChartSplitRange[]
  /** Epoch-ms of the baseline→after boundary, for the marker line. */
  testBoundaryMs: number | null
  label: string
  /** The model's name on its own, without the "· expected ±kσ" caption. The
   *  hover readout prefixes it to a value, where the caption would be noise. */
  name: string
  /** The k the band is drawn at: the model's alert threshold. */
  bandSigmas: number
  /** Holes narrower than this bridge like the pens; null with no series. */
  gapToleranceMs: number | null
  /** True when the plant has left the band's regime; excursion marks are then
   *  a statement about the model, not the asset, and stay off the chart. */
  outOfRange: boolean
  /** The saved-output pen that draws this model's expected value, when it is on
   *  the chart. See `SentinelBandOverlay.centerPenId`. */
  centerPenId: string | null
  /** The fit itself, in the KPI's own units: expected ±1σ against actual. */
  band: {
    xs: number[]
    center: (number | null)[]
    upper: (number | null)[]
    lower: (number | null)[]
    actual: (number | null)[]
    sigma: (number | null)[]
  } | null
}

const EMPTY: AgentChartOverlay = {
  target: null,
  splitRanges: [],
  testBoundaryMs: null,
  label: '',
  name: '',
  bandSigmas: BAND_SIGMAS,
  gapToleranceMs: null,
  outOfRange: false,
  centerPenId: null,
  band: null,
}

let current: AgentChartOverlay = EMPTY
const listeners = new Set<() => void>()

const emit = () => listeners.forEach((listener) => listener())

const clearCurrent = (): void => {
  if (current === EMPTY) return
  current = EMPTY
  emit()
}

/** Publish (or clear, with `null`) the model whose windows the chart shades. */
export const setAgentChartOverlay = (
  overlay: {
    target: string | null
    baselineStart?: string
    baselineEnd?: string
    label?: string
    name?: string
    centerPenId?: string | null
    /** The rule's level threshold, so the band is the line the rule watches. */
    bandSigmas?: number
    health?: { status?: string } | null
    /** Daily fit series (dates + actual/expected + σ scale). */
    series?: {
      dates: string[]
      actual: (number | null)[]
      expected: (number | null)[]
      err_scale: number
      smoothing_days?: number
    } | null
  } | null
): void => {
  if (!overlay?.target || !overlay.baselineStart || !overlay.baselineEnd) {
    clearCurrent()
    return
  }
  const startMs = Date.parse(overlay.baselineStart)
  const endMs = Date.parse(overlay.baselineEnd)
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  ) {
    // A baseline the chart cannot place is "no overlay" — keeping the previous
    // model's band and tints under the new model's card would be worse.
    clearCurrent()
    return
  }
  // The band is drawn at the rule's k, not ±1: one sigma of the daily fit error
  // can be sub-pixel against the signal, and what you see should be the line
  // the alert actually watches.
  const bandSigmas =
    overlay.bandSigmas && overlay.bandSigmas > 0
      ? overlay.bandSigmas
      : BAND_SIGMAS
  const scale = (overlay.series?.err_scale ?? 0) * bandSigmas
  const band = overlay.series
    ? {
        // A daily fit is stamped with a date, not an instant, so it parses to
        // midnight — the START of the day it summarises. Anchoring it at midday
        // instead centres each value over its own day, which is what makes a
        // cursor at 20:04 read that day's expectation rather than the next
        // day's (nearest-bucket lookup, cadence 24h). It also lines the drawn
        // line up with the expected PEN, which is keyed by calendar day.
        xs: overlay.series.dates.map((d) =>
          /^\d{4}-\d{2}-\d{2}$/.test(d)
            ? Date.parse(d) + DAY_MS / 2
            : Date.parse(d)
        ),
        center: overlay.series.expected,
        upper: overlay.series.expected.map((v) =>
          v === null ? null : v + scale
        ),
        lower: overlay.series.expected.map((v) =>
          v === null ? null : v - scale
        ),
        actual: overlay.series.actual,
        // Sigma stays in single-sigma units — it is the number quoted in the
        // card and the alert rule, and rescaling it here would make the two
        // disagree.
        sigma: overlay.series.actual.map((a, i) => {
          const e = overlay.series?.expected[i]
          const oneSigma = overlay.series?.err_scale ?? 0
          return a === null || e === null || e === undefined || !oneSigma
            ? null
            : (a - e) / oneSigma
        }),
      }
    : null
  current = {
    band,
    target: overlay.target,
    // 'train' shades the baseline, 'test' the graded span after it — the same
    // two roles the chart already knows how to tint and label.
    splitRanges: [
      { startMs, endMs, role: 'train' },
      { startMs: endMs, endMs: Infinity, role: 'test' },
    ],
    testBoundaryMs: endMs,
    label: overlay.label ?? '',
    name: overlay.name ?? overlay.label ?? '',
    bandSigmas,
    gapToleranceMs: band
      ? gapToleranceMs(band.xs, overlay.series?.smoothing_days ?? 0)
      : null,
    outOfRange: overlay.health?.status === 'out_of_range',
    centerPenId: overlay.centerPenId ?? null,
  }
  emit()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const snapshot = () => current

/** Chart-facing read of the published agent-model windows. */
export const useAgentChartOverlay = (): AgentChartOverlay =>
  useSyncExternalStore(subscribe, snapshot, snapshot)

/**
 * The published model as a band overlay — the shape the chart plugin draws and
 * the hover readout reads.
 *
 * One builder for both on purpose. The dashed expected line was drawn straight
 * onto the canvas from this store while the readout only knew about sentinels
 * and the Experiment Lab, so the line had no value you could hover: it was
 * paint, not a reading. Anything that consumes an overlay now gets the same
 * object, so a line on the chart and a number in the readout cannot disagree.
 */
export const agentBandOverlay = (
  overlay: AgentChartOverlay
): SentinelBandOverlay | null => {
  if (!overlay.splitRanges.length || !overlay.target) return null
  return {
    targetSensorId: overlay.target,
    sentinelName: overlay.name || overlay.label,
    method: '',
    label: overlay.label,
    color: AGENT_BAND_COLOR,
    // The band IS the model's fit, in the KPI's own units, drawn on the
    // derived KPI pen — the same shape sentinels use on a tag pen.
    xs: overlay.band?.xs ?? [],
    upper: overlay.band?.upper ?? [],
    lower: overlay.band?.lower ?? [],
    center: overlay.band?.center ?? [],
    actual: overlay.band?.actual ?? [],
    sigma: overlay.band?.sigma ?? [],
    trainedSplitMs: overlay.testBoundaryMs,
    modelCreatedMs: null,
    windowStartMs: null,
    splitRanges: overlay.splitRanges,
    centerPenId: overlay.centerPenId,
    // The glow marks the band that is drawn, at the k the rule uses.
    warnSigma: overlay.bandSigmas,
    showOutOfBandMarks: !overlay.outOfRange,
    gapToleranceMs: overlay.gapToleranceMs ?? undefined,
  }
}

/** The published model as a band overlay, or null when none is shown. Memoised
 *  on the store snapshot, which only changes when a model is published, so
 *  consumers can use it as a dependency without re-running every render. */
export const useAgentBandOverlay = (): SentinelBandOverlay | null => {
  const overlay = useAgentChartOverlay()
  return useMemo(() => agentBandOverlay(overlay), [overlay])
}
